import { useCallback, useEffect, useState } from 'react';
import {
  api,
  type AssetWithPlan,
  type Company,
  type DepreciationMethod,
  type FixedAsset,
  type NewAsset,
  type SkippedAsset,
} from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount, date, money, periodShort } from '../format';
import * as Icon from '../components/Icons';
import { ErrorAlert, failureOf, type Failure } from '../components/Alerts';
import type { Panel } from '../components/Shell';

const today = () => new Date().toISOString().slice(0, 10);
const previousMonth = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1)).toISOString().slice(0, 7);
};

const METHODS: DepreciationMethod[] = ['LINIOWA', 'JEDNORAZOWA', 'DEGRESYWNA'];

function emptyAsset(): NewAsset {
  return {
    name: '',
    inventoryNumber: '',
    kstCode: '',
    kind: 'TANGIBLE',
    acquiredOn: today(),
    inServiceOn: today(),
    initialValue: '0.00',
    method: 'LINIOWA',
    annualRate: '0.20',
  };
}

export function AssetsPanel({
  company,
  t,
  locale,
  onGo,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  onGo?: (panel: Panel) => void;
}) {
  const [assets, setAssets] = useState<FixedAsset[] | null>(null);
  const [includeDisposed, setIncludeDisposed] = useState(false);
  const [form, setForm] = useState<NewAsset | null>(null);
  const [details, setDetails] = useState<AssetWithPlan | null>(null);
  const [month, setMonth] = useState(previousMonth);
  const [message, setMessage] = useState<string | null>(null);
  /* Środki, których przebieg nie zaksięgował w dzienniku — z powodem, zamiast po cichu */
  const [skipped, setSkipped] = useState<SkippedAsset[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setAssets(await api.assets(company.id, includeDisposed));
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [company.id, includeDisposed]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  const editable = !company.roles.every((role) => role === 'VIEWER' || role === 'BOARD_MEMBER');
  const empty = assets?.length === 0;

  if (form) {
    return (
      <>
        <div className="hd">
          <div>
            <h3>{t.assets.add}</h3>
            <p>{t.assets.lead}</p>
          </div>
        </div>
        <ErrorAlert error={error} t={t} onGo={onGo} />
        <form
          className="panel"
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              await api.addAsset(company.id, form);
              setForm(null);
            });
          }}
        >
          <div className="fields">
            <div className="field">
              <label htmlFor="aName">{t.assets.name}</label>
              <input
                id="aName"
                className="inp"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="aInv">{t.assets.inventoryNumber}</label>
              <input
                id="aInv"
                className="inp mono"
                value={form.inventoryNumber ?? ''}
                onChange={(event) => setForm({ ...form, inventoryNumber: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="aKst">{t.assets.kstCode}</label>
              <input
                id="aKst"
                className="inp mono"
                value={form.kstCode ?? ''}
                onChange={(event) => setForm({ ...form, kstCode: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="aAcq">{t.assets.acquired}</label>
              <input
                id="aAcq"
                className="inp"
                type="date"
                value={form.acquiredOn}
                onChange={(event) => setForm({ ...form, acquiredOn: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="aService">{t.assets.inService}</label>
              <input
                id="aService"
                className="inp"
                type="date"
                value={form.inServiceOn}
                onChange={(event) => setForm({ ...form, inServiceOn: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="aValue">{t.assets.value}</label>
              <input
                id="aValue"
                className="inp num"
                value={form.initialValue}
                onChange={(event) => setForm({ ...form, initialValue: event.target.value })}
                inputMode="decimal"
                required
              />
            </div>
            <div className="field">
              <label htmlFor="aMethod">{t.assets.method}</label>
              <select
                id="aMethod"
                className="inp"
                value={form.method}
                onChange={(event) => setForm({ ...form, method: event.target.value as DepreciationMethod })}
              >
                {METHODS.map((method) => (
                  <option key={method} value={method}>
                    {t.assets.methods[method]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="aRate">{t.assets.rate}</label>
              <input
                id="aRate"
                className="inp num"
                value={form.annualRate}
                onChange={(event) => setForm({ ...form, annualRate: event.target.value })}
                inputMode="decimal"
              />
              <span className="hint">{t.assets.rateHint}</span>
            </div>
          </div>
          <div className="bar2">
            <button type="submit" className="btn" disabled={busy}>
              {busy ? t.companies.saving : t.assets.save}
            </button>
            <div className="sp">
              <button type="button" className="btn ghost" onClick={() => setForm(null)} disabled={busy}>
                {t.companies.cancel}
              </button>
            </div>
          </div>
        </form>
      </>
    );
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.assets.title}</h3>
          <p>{t.assets.lead}</p>
        </div>
        <div className="sp">
          {/* Przycisk księgowania odpisów znika, dopóki ewidencja jest pusta — nie ma czego księgować */}
          {editable && !empty && (
            <>
              <input
                className="inp w"
                type="month"
                aria-label={t.assets.month}
                value={month}
                onChange={(event) => setMonth(event.target.value)}
              />
              <button
                type="button"
                className="btn sec"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    const result = await api.bookDepreciation(company.id, month);
                    /*
                     * Zero odpisów to normalna sytuacja: środek mógł jeszcze nie zacząć się
                     * amortyzować. Przy pełnych księgach mówimy osobno, ile zapisów weszło do
                     * dziennika — to dwie ewidencje i mogą się różnić.
                     */
                    setMessage(
                      `${t.assets.booked}: ${result.booked} · ${money(result.total, locale)}` +
                        (company.booksType === 'FULL' ? ` · ${t.assets.posted}: ${result.posted}` : ''),
                    );
                    setSkipped(result.skipped);
                  })
                }
              >
                {t.assets.bookDepreciation}
              </button>
            </>
          )}
          {editable && (
            <button type="button" className="btn" onClick={() => setForm(emptyAsset())}>
              <Icon.Plus />
              {t.assets.add}
            </button>
          )}
        </div>
      </div>

      <ErrorAlert error={error} t={t} onGo={onGo} />
      {message && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{message}</b>
          </div>
        </div>
      )}
      {skipped.length > 0 && (
        <div className="alert a-warn">
          <Icon.Warn />
          <div>
            <b>{t.assets.skipped}</b>
            <ul>
              {skipped.map((asset) => (
                <li key={`${asset.name}-${asset.reason}`}>
                  {asset.name}
                  {asset.inventoryNumber && ` (${asset.inventoryNumber})`}: {asset.reason}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="row">
          <label className="check">
            <input
              type="checkbox"
              checked={includeDisposed}
              onChange={(event) => setIncludeDisposed(event.target.checked)}
            />
            {t.assets.includeDisposed}
          </label>
          <span className="sm muted" style={{ marginLeft: 'auto' }}>
            {assets?.length ?? 0}
          </span>
        </div>

        {empty ? (
          <div className="empty">
            <Icon.Asset />
            <b>{t.assets.none}</b>
            <p>{t.assets.noneHint}</p>
            <div className="row" style={{ justifyContent: 'center' }}>
              <button type="button" className="btn" onClick={() => setForm(emptyAsset())}>
                {t.assets.add}
              </button>
              {onGo && (
                <button type="button" className="btn ghost" onClick={() => onGo('costs')}>
                  {t.costs.new}
                </button>
              )}
            </div>
          </div>
        ) : (
          assets && (
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>{t.assets.name}</th>
                    <th>{t.assets.inventoryNumber}</th>
                    <th className="n">{t.assets.kstCode}</th>
                    <th>{t.assets.inService}</th>
                    <th className="n">{t.assets.value}</th>
                    <th>{t.assets.method}</th>
                    <th className="n">{t.assets.rate}</th>
                    <th>{t.costs.status}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {assets.map((asset) => (
                    <tr key={asset.id}>
                      <td>{asset.name}</td>
                      <td className="mono">{asset.inventoryNumber ?? '—'}</td>
                      <td className="n">{asset.kstCode ?? '—'}</td>
                      <td className="doc">{date(asset.inServiceOn, locale)}</td>
                      <td className="n">{amount(asset.initialValue, locale)}</td>
                      <td>{t.assets.methods[asset.method]}</td>
                      <td className="n">{asset.annualRate ?? '—'}</td>
                      <td>
                        {asset.disposedOn ? (
                          <span className="chip st-draft">
                            <i />
                            {t.assets.disposed} {date(asset.disposedOn, locale)}
                          </span>
                        ) : (
                          <span className="chip st-ok">
                            <i />
                            {t.assets.depreciating}
                          </span>
                        )}
                      </td>
                      <td className="act">
                        <div className="row">
                          <button
                            type="button"
                            className="btn ghost sm"
                            disabled={busy}
                            onClick={() => void run(async () => setDetails(await api.asset(company.id, asset.id)))}
                          >
                            {t.assets.showPlan}
                          </button>
                          {editable && !asset.disposedOn && (
                            <button
                              type="button"
                              className="btn danger sm"
                              disabled={busy}
                              onClick={() => {
                                const reason = window.prompt(t.assets.disposalReason);
                                if (!reason) return;
                                void run(async () => {
                                  const result = await api.disposeAsset(company.id, asset.id, today(), reason);
                                  // Wycofanie z ewidencji zostaje nawet wtedy, gdy zapisu nie dało się
                                  // utworzyć — wtedy mówimy, czego w księgach nie ma
                                  setMessage(
                                    result.writeOff
                                      ? `${t.assets.removedFromBooks} · ${t.assets.removedAccumulated}: ${money(
                                          result.writeOff.accumulated,
                                          locale,
                                        )} · ${t.assets.removedNet}: ${money(result.writeOff.netValue, locale)} · ${
                                          t.assets.removedEntry
                                        } ${result.writeOff.entryNo}`
                                      : result.note
                                        ? `${t.assets.removedMissing} ${result.note}`
                                        : null,
                                  );
                                });
                              }}
                            >
                              {t.assets.dispose}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {details && (
        <div className="panel">
          <header>
            <h4>
              {t.assets.plan}: {details.asset.name}
            </h4>
            <div className="sp">
              <span className="sm muted">
                {t.assets.inLedger}: {money(details.bookedTotal, locale)}
                {details.bookedThrough && ` ${t.assets.through} ${periodShort(details.bookedThrough)}`}
                {company.booksType === 'FULL' && (
                  <>
                    {' · '}
                    {t.assets.inJournal}: {money(details.postedTotal, locale)}
                    {details.postedThrough && ` ${t.assets.through} ${periodShort(details.postedThrough)}`}
                  </>
                )}
              </span>
              <button type="button" className="btn ghost sm" onClick={() => setDetails(null)}>
                {t.assets.hidePlan}
              </button>
            </div>
          </header>
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.assets.period}</th>
                  <th className="n">{t.assets.writeOff}</th>
                  <th className="n">{t.assets.remaining}</th>
                </tr>
              </thead>
              <tbody>
                {details.plan.map((entry) => (
                  <tr key={entry.period}>
                    <td className="doc">{periodShort(entry.period)}</td>
                    <td className="n">{amount(entry.amount, locale)}</td>
                    <td className="n">{amount(entry.remaining, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
